import { compose } from 'redux';
import { connect } from 'react-redux';
import valid from 'card-validator';
import store from '@/store';
import { reduxForm } from 'redux-form';
import CardForm from './CardForm';

/**
 * Validates card form fields based on the card structure being used.
 * Supports both old structure (separate month/year) and new structure (unified date).
 *
 * @param {Object} fields - The form fields to validate.
 * @param {string} fields.holderName - The card holder's name.
 * @param {string} fields.cardNumber - The card number.
 * @param {string} fields.cvv2 - The CVV2 code.
 * @param {string} fields.expirationMonth - The expiration month (old structure).
 * @param {string} fields.expirationYear - The expiration year (old structure).
 * @param {string} fields.expirationDate - The expiration date in MM/YY format (new structure).
 * @param {string} fields.billingAddress - The billing address.
 * @returns {Object} Validation errors object.
 */
const validate = ({
  holderName,
  cardNumber,
  cvv2,
  expirationMonth,
  expirationYear,
  expirationDate,
  billingAddress,
}) => {
  const errors = {};
  const { features } = store.getState().whitelabelConfig;

  if (!holderName) {
    errors.holderName = 'Required';
  }

  if (features.BILLING_ADDRESS_ENABLED && !billingAddress) {
    errors.billingAddress = 'Required billing address';
  }

  const validatedCardNumber = valid.number(cardNumber);
  if (!validatedCardNumber.isValid) {
    errors.cardNumber = 'Invalid card number';
  }

  const amexCVVDigits = 4;
  const cvvIsValidForAmex = valid.cvv(cvv2, amexCVVDigits).isValid;
  const cvvIsValidForOtherBrands = valid.cvv(cvv2).isValid;
  if (!(cvvIsValidForAmex || cvvIsValidForOtherBrands)) {
    errors.cvv2 = 'Invalid CVC';
  }

  // Check if using new card structure (unified expiration date)
  // Both feature flags must be true
  const USE_NEW_CARD_INPUT = features?.USE_NEW_CARD_INPUT ?? false;
  const USE_NEW_CARD_STRUCTURE = features?.USE_NEW_CARD_STRUCTURE ?? false;
  const isNewCardStructure = USE_NEW_CARD_INPUT && USE_NEW_CARD_STRUCTURE && expirationDate;

  if (isNewCardStructure) {
    // Validate unified expiration date format (MM/YY)
    if (!expirationDate || !valid.expirationDate(expirationDate).isValid) {
      errors.expirationDate = 'Invalid Date';
    }
  } else {
    // Validate separate month and year fields (old structure)
    if (!expirationMonth || !valid.expirationMonth(expirationMonth).isValid) {
      errors.expirationMonth = 'Invalid expiry';
    }
    if (!expirationYear || !valid.expirationYear(expirationYear).isValid) {
      errors.expirationYear = 'Invalid expiry';
    }
  }

  return errors;
};

const mapStateToProps = (state) => {
  const { selectedInstallmentsPlan } = state.purchase.toJS();
  const { months } = selectedInstallmentsPlan;

  return {
    initialValues: {
      paymentPlan: months > 1 ? 'installments' : 'single',
      monthlySelectedPlan: months,
      cardBrand: '',
    },
  };
};

export default compose(
  connect(mapStateToProps),
  reduxForm({
    form: 'card',
    validate,
  }),
)(CardForm);
